import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import "@/App.css";
import { ThemeProvider } from "@/hooks/useTheme";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { PricingProvider } from "@/context/PricingContext";
import CartDrawer from "@/components/CartDrawer";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import StructuredData from "@/components/StructuredData";
import Navigation from "@/components/Navigation";
import SEOHead from "@/components/SEOHead";
import HeroSection from "@/components/HeroSection";
import TrustStrip from "@/components/TrustStrip";
import WhySection from "@/components/WhySection";
import PromiseSection from "@/components/PromiseSection";
import IndustriesSection from "@/components/IndustriesSection";
import AdminPanelUSP from "@/components/AdminPanelUSP";
import ResultsSection from "@/components/ResultsSection";
import Footer from "@/components/Footer";
import AnimatedBackground from "@/components/AnimatedBackground";
import CookieConsent from "@/components/CookieConsent";
import BackToTop from "@/components/BackToTop";
import ScrollToTop from "@/components/ScrollToTop";
import AdminPage from "@/components/AdminPage";
import AuthCallback from "@/components/AuthCallback";
import ServicesPage from "@/pages/ServicesPage";
import PricingPage from "@/pages/PricingPage";
import ContactPage from "@/pages/ContactPage";
import FaqPage from "@/pages/FaqPage";
import WebDesignBournemouth from "@/pages/WebDesignBournemouth";
import WebDesignBarbers from "@/pages/WebDesignBarbers";
import WebDesignGyms from "@/pages/WebDesignGyms";
import WebDesignRestaurants from "@/pages/WebDesignRestaurants";
import WebDesignTrades from "@/pages/WebDesignTrades";
import BlogIndex from "@/pages/BlogIndex";
import BlogPost from "@/pages/BlogPost";
import ShopPage from "@/pages/ShopPage";
import ProductPage from "@/pages/ProductPage";
import OrderTrackPage from "@/pages/OrderTrackPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import NotFoundPage from "@/pages/NotFoundPage";
import AdminDemoPage from "@/pages/AdminDemoPage";
import AiSitemapPage from "@/pages/AiSitemapPage";
import CursorTrail from "@/components/CursorTrail";

function LandingPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A] relative transition-colors duration-300">
      <Navigation />
      <AnimatedBackground />
      <div className="relative z-10 page-load">
        <SEOHead
          title="Web Design Bournemouth | Affordable Websites & Hosting | UI DESIGNS"
          description="Affordable web design and managed hosting for small businesses in Bournemouth, Poole, and Christchurch. Cheap websites that look premium. You & I, designing together."
          canonical="https://uidesigns.co.uk"
          keywords="web design Bournemouth, cheap web design, affordable website design, website designer Bournemouth, cheap websites Poole Christchurch, managed website hosting, small business websites"
        />
        <main>
          <HeroSection />
          <TrustStrip />
          <WhySection />
          <ResultsSection />
          <AdminPanelUSP />
          <IndustriesSection />
          <PromiseSection />
        </main>
        <Footer />
      </div>
      <CookieConsent />
      <BackToTop />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  // CRITICAL: Check URL fragment for session_id SYNCHRONOUSLY during render
  // This prevents race conditions with AuthProvider's /me check
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/faq" element={<FaqPage />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/shop/:slug" element={<ProductPage />} />
      <Route path="/track" element={<OrderTrackPage />} />
      <Route path="/track/:orderId" element={<OrderTrackPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/web-design-bournemouth" element={<WebDesignBournemouth />} />
      <Route path="/web-design-barbers-bournemouth" element={<WebDesignBarbers />} />
      <Route path="/web-design-gyms-bournemouth" element={<WebDesignGyms />} />
      <Route path="/web-design-restaurants-bournemouth" element={<WebDesignRestaurants />} />
      <Route path="/web-design-trades-bournemouth" element={<WebDesignTrades />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/admin-demo" element={<AdminDemoPage />} />
      <Route path="/ai-sitemap" element={<AiSitemapPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ThemeProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AuthProvider>
            <PricingProvider>
            <CartProvider>
              <AppRouter />
              <CartDrawer />
              <AnalyticsTracker />
              <StructuredData />
              <CursorTrail />
              <Toaster
                theme="dark"
                position="top-center"
                toastOptions={{
                  style: {
                    background: '#0D0D0D',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: '#fff',
                  },
                }}
              />
            </CartProvider>
            </PricingProvider>
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
